import {
  Box,
  Flex,
  FormCheckbox,
  FormGroup,
  FormRadio,
  Text,
} from '@spotify-internal/encore-web';
import { FormTextField } from '@spotify/confidence-core-react';

import {
  useAccountDetailsContext,
  useAuthConnectionsContext,
} from './CreateAccountFormProvider';
import type { ToggleAuthConnectionOptions } from './hooks/useAuthConnections';
import type { AuthConnection } from './hooks/useCreateAccount';
import { getGoogleLoginMode } from './signupUtils';

const AUTH_CONNECTION_OPTIONS: { connection: AuthConnection; label: string }[] =
  [
    { connection: 'passwordAuthConnection', label: 'Email' },
    { connection: 'googleAuthConnection', label: 'Google' },
  ];

type AuthConnectionsFieldsProps = {
  authConnections: Record<AuthConnection, boolean>;
  hasSelectedAuthConnection: boolean;
  loginDomain?: string;
  userAuthConnection: AuthConnection;
  onLoginDomainChange: (loginDomain?: string) => void;
  onToggleAuthConnection: (options: ToggleAuthConnectionOptions) => void;
};

const AuthConnectionsFields = ({
  authConnections,
  hasSelectedAuthConnection,
  loginDomain,
  userAuthConnection,
  onLoginDomainChange,
  onToggleAuthConnection,
}: AuthConnectionsFieldsProps) => (
  <>
    <FormGroup label="Login methods" hideLabel withFieldset>
      {AUTH_CONNECTION_OPTIONS.map(({ connection, label }) => (
        <FormCheckbox
          id={`auth-connection-${connection}`}
          key={connection}
          checked={authConnections[connection]}
          required={!hasSelectedAuthConnection}
          disabled={userAuthConnection === connection}
          onChange={() =>
            onToggleAuthConnection({
              connection,
              checked: !authConnections[connection],
            })
          }
        >
          {label}
          {userAuthConnection === connection
            ? ' (you used this when signing up)'
            : ''}
        </FormCheckbox>
      ))}
    </FormGroup>

    {authConnections.googleAuthConnection && (
      <FormGroup label="Google login options" withFieldset>
        <FormRadio
          id="google-login-invite-only"
          name="google-login-option"
          value="invite-only"
          checked={getGoogleLoginMode(loginDomain) === 'invite-only'}
          onChange={() => onLoginDomainChange(undefined)}
        >
          Only invited users can log in
        </FormRadio>
        <FormRadio
          id="google-login-domain-option"
          name="google-login-option"
          value="domain"
          checked={getGoogleLoginMode(loginDomain) === 'domain'}
          onChange={() => onLoginDomainChange('')}
        >
          Allow everyone from a specific domain to log in
        </FormRadio>
        {getGoogleLoginMode(loginDomain) === 'domain' && (
          <Box paddingBlockStart="tighter-2">
            <FormTextField
              label="Google login domain"
              name="google-login-domain"
              placeholder="For example spotify.com"
              value={loginDomain}
              onChange={event => onLoginDomainChange(event.currentTarget.value)}
            />
          </Box>
        )}
      </FormGroup>
    )}
  </>
);

export const AuthConnectionsSection = () => {
  const { accountDetails, setField } = useAccountDetailsContext();
  const {
    hasSelectedAuthConnection,
    userAuthConnection,
    toggleAuthConnection,
  } = useAuthConnectionsContext();

  return (
    <Flex direction="column" gap="tighter-2">
      <Text variant="bodyMedium" semanticColor="textSubdued">
        Choose how people in your company will log in to Confidence.
      </Text>
      <AuthConnectionsFields
        authConnections={accountDetails.authConnections}
        hasSelectedAuthConnection={hasSelectedAuthConnection}
        loginDomain={accountDetails.loginDomain}
        userAuthConnection={userAuthConnection}
        onLoginDomainChange={loginDomain => setField({ loginDomain })}
        onToggleAuthConnection={toggleAuthConnection}
      />
    </Flex>
  );
};
